import React, { useState, useEffect } from 'react';
import { BarChart3, CheckCircle2, XCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = '';

const ParsingStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBank, setExpandedBank] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/parsing-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Error al cargar estadísticas');
      
      const data = await response.json();
      console.log('Parsing stats data:', data);
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching parsing stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const getSuccessColor = (rate) => {
    if (rate >= 90) return 'text-green-600 bg-green-100';
    if (rate >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getProgressColor = (rate) => {
    if (rate >= 90) return 'bg-green-500';
    if (rate >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Diagnóstico de Parsing por Banco</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin mb-3" />
          <span className="text-gray-600 font-medium">Cargando estadísticas...</span>
          <span className="text-sm text-gray-400 mt-1">
            Este proceso puede tomar unos segundos
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Diagnóstico de Parsing por Banco</h3>
        </div>
        <div className="text-red-500 text-center py-4">{error}</div>
      </div>
    );
  }

  // Show message when cache has no data
  if (stats.total_emails === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Diagnóstico de Parsing por Banco</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin mb-3" />
          <span className="text-gray-600 font-medium">Actualizando caché de emails...</span>
          <span className="text-sm text-gray-400 mt-1">
            Este proceso puede tomar varios minutos
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Diagnóstico de Parsing por Banco</h3>
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Actualizar"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-3">
          <div className="text-xs text-cyan-600 font-medium">Total Emails</div>
          <div className="text-2xl font-bold text-cyan-700">{stats?.total_emails || 0}</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3">
          <div className="text-xs text-green-600 font-medium">Con Nombre</div>
          <div className="text-2xl font-bold text-green-700">{stats?.total_with_name || 0}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
          <div className="text-xs text-blue-600 font-medium">Con Monto</div>
          <div className="text-2xl font-bold text-blue-700">{stats?.total_with_amount || 0}</div>
        </div>
        <div className={`rounded-lg p-3 ${getSuccessColor(stats?.overall_success_rate || 0)}`}>
          <div className="text-xs font-medium">Tasa de Éxito</div>
          <div className="text-2xl font-bold">{stats?.overall_success_rate || 0}%</div>
        </div>
      </div>

      {/* Bank Stats Table */}
      <div className="space-y-2">
        <div className="text-sm font-medium text-gray-600 mb-2">Estadísticas por Banco</div>
        
        {stats?.by_bank?.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No hay datos de parsing disponibles
          </div>
        ) : (
          stats?.by_bank?.map((bank) => (
            <div key={bank.bank} className="border border-gray-100 rounded-lg overflow-hidden">
              <div 
                className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => setExpandedBank(expandedBank === bank.bank ? null : bank.bank)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-cyan-700">
                      {bank.bank.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">{bank.bank}</div>
                    <div className="text-xs text-gray-500">
                      {bank.with_name} de {bank.total} emails
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-32 hidden sm:block">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${getProgressColor(bank.success_rate)} transition-all`}
                        style={{ width: `${bank.success_rate}%` }}
                      />
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-sm font-medium ${getSuccessColor(bank.success_rate)}`}>
                    {bank.success_rate}%
                  </div>
                  {bank.success_rate >= 90 ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : bank.success_rate >= 70 ? (
                    <CheckCircle2 className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  {expandedBank === bank.bank ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedBank === bank.bank && bank.recent_senders?.length > 0 && (
                <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50">
                  <div className="text-xs font-medium text-gray-500 mt-2 mb-1">
                    Remitentes recientes:
                  </div>
                  <div className="space-y-1">
                    {bank.recent_senders.map((sender, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-gray-700">{sender.name}</span>
                        <span className="text-green-600 font-medium">
                          ${sender.amount?.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>90%+ Excelente</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span>70-89% Revisar</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>&lt;70% Problema</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParsingStatsPanel;
